import { type DateRange, DateRangePicker } from '@/Components/daterange-picker';
import type { DateInput } from '@/graphql/types';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Grow, Paper, Popper } from '@mui/material';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

import './DateFilter.less';

// Define schemas
const dateInputSchema = z.object({
  day: z.number(),
  month: z.number(),
  year: z.number(),
});

const dateSearchSchema = z.object({
  date: z
    .object({
      after: dateInputSchema,
      before: dateInputSchema,
    })
    .nullable()
    .optional(),
});

const dateComponentProps = z.object({
  displayName: z.string().optional(),
  minDate: z.string().optional(),
  maxDate: z.string().optional(),
});

type DateFilterProps = z.infer<typeof dateComponentProps>;

// Helper functions for date conversion
const convertDateInputToDate = (dateInput?: DateInput | null): Date | null => {
  if (!dateInput?.year || !dateInput?.month || !dateInput?.day) return null;
  return new Date(dateInput.year, dateInput.month - 1, dateInput.day);
};

const areDateRangesEqual = (a: DateRange | null, b: DateRange | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;

  return a.startDate?.getTime() === b.startDate?.getTime() && a.endDate?.getTime() === b.endDate?.getTime();
};

const DateFilter = ({ displayName = 'Date', minDate = '2021-01-01', maxDate }: DateFilterProps) => {
  const { fastFilters, updateFilter, isUpdating } = __useFilterState(dateSearchSchema);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const isUpdatingRef = useRef(false);
  const lastAppliedDateRangeRef = useRef<DateRange | null>(null);

  // State
  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  useEffect(() => {
    if (isUpdatingRef.current) return;

    const dateParam = fastFilters.date;
    let newDateRange: DateRange | null = null;

    if (dateParam?.after && dateParam?.before) {
      const startDate = convertDateInputToDate(dateParam.after);
      const endDate = convertDateInputToDate(dateParam.before);

      if (startDate && endDate) {
        newDateRange = { startDate, endDate };
      }
    }

    setDateRange(newDateRange);
  }, [fastFilters?.date]);

  const applyDateRange = useCallback(
    (range: DateRange | null) => {
      if (isUpdatingRef.current) return;
      if (areDateRangesEqual(range, lastAppliedDateRangeRef.current)) return;

      lastAppliedDateRangeRef.current = range;

      // Set flag to avoid re-entrancy
      isUpdatingRef.current = true;

      try {
        if (range?.startDate && range?.endDate) {
          const newDateValue = {
            after: {
              day: range.startDate.getDate(),
              month: range.startDate.getMonth() + 1,
              year: range.startDate.getFullYear(),
            },
            before: {
              day: range.endDate.getDate(),
              month: range.endDate.getMonth() + 1,
              year: range.endDate.getFullYear(),
            },
          };

          updateFilter({
            date: newDateValue,
          });
        } else {
          updateFilter({
            date: undefined,
          });
        }
      } finally {
        requestAnimationFrame(() => {
          isUpdatingRef.current = false;
        });
      }
    },
    [updateFilter]
  );

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    applyDateRange(dateRange);
  }, [applyDateRange, dateRange]);

  const handleDateRangeChange = useCallback((range: DateRange | null) => {
    if (range?.startDate && range?.endDate && range.startDate.getTime() === range.endDate.getTime()) {
      setDateRange(null);
      return;
    }

    setDateRange(range);
  }, []);

  const buttonText = useMemo(() => {
    return dateRange?.startDate && dateRange?.endDate
      ? `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`
      : displayName;
  }, [dateRange, displayName]);

  return (
    <div style={{ position: 'relative' }}>
      <Button className="z-btn-6" onClick={handleToggle} ref={anchorRef} endIcon={<ArrowDropDownIcon />}>
        {buttonText}
      </Button>

      {open && (
        <Popper open={true} anchorEl={anchorRef.current} placement="bottom" style={{ zIndex: 2000 }} transition>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper>
                <DateRangePicker
                  className="zngly-date-range-picker"
                  definedRanges={[]}
                  open={true}
                  onClose={handleClose}
                  initialDateRange={dateRange || undefined}
                  onChange={handleDateRangeChange}
                  minDate={new Date(minDate)}
                  maxDate={maxDate ? new Date(maxDate) : new Date()}
                />
              </Paper>
            </Grow>
          )}
        </Popper>
      )}

      <FilterBadge count={!!dateRange} isUpdating={isUpdating} />
    </div>
  );
};

const DateFilterMemo = React.memo(DateFilter);

export const FilterConfig_Date = {
  date: {
    searchSchema: dateSearchSchema,
    component: DateFilterMemo,
    componentProps: dateComponentProps,
  },
} as const;
